import type { ComponentProps } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

const keyCapVariants = cva("rounded-control-sm px-1", {
  variants: {
    variant: {
      /** Settings rows and tooltips. */
      subtle: "bg-[var(--color-surface-selected)] font-sans text-secondary",
      /** Menu rows on glass. */
      raised:
        "border border-glass-stroke-light bg-glass-fill-heavy text-appkit-mini font-semibold leading-none text-secondary",
    },
    size: {
      /** At least a square cap, the key centred. */
      fixed: "grid h-5 min-w-5 place-items-center",
      /** As tall as the surrounding text. */
      inline: "",
    },
  },
  defaultVariants: { variant: "subtle", size: "fixed" },
});

const GAP_CLASS = { subtle: "gap-0.5", raised: "gap-1" } as const;

export interface ShortcutKeyCapsProps
  extends
    Omit<ComponentProps<"span">, "children">,
    VariantProps<typeof keyCapVariants> {
  /** The keys in order, as `shortcutKeys()` names them. */
  keys: readonly string[];
  /** Spoken shortcut; with it the caps read as one image of that name. */
  label?: string;
}

/** A keyboard shortcut drawn as one cap per key. */
export function ShortcutKeyCaps({
  keys,
  label,
  variant,
  size,
  className,
  ...props
}: ShortcutKeyCapsProps) {
  return (
    <span
      {...props}
      {...(label ? { role: "img", "aria-label": label } : {})}
      className={cn(
        "flex items-center",
        GAP_CLASS[variant ?? "subtle"],
        className,
      )}
    >
      {keys.map((key, index) => (
        <kbd
          key={`${key}-${index}`}
          aria-hidden={label ? true : undefined}
          className={keyCapVariants({ variant, size })}
        >
          {key}
        </kbd>
      ))}
    </span>
  );
}
